import React, { useMemo } from "react";
import {
  Button,
  ColorTokens,
  Icon,
  breakpoints,
  useColorTokens,
  useMediaQuery
} from "@tvg/design-system";
import { get } from "lodash";
import { RaceProgram, RaceVideoFeedback } from "@tvg/ts-types/Race";
import RaceHeaderUtils from "@tvg/utils/RaceHeaderUtils";
import { Container } from "./styled-components";
import VideoButton from "../../../TrackHeader/VideoButton";

interface ActionButtonsProps {
  race: RaceProgram;
  races?: RaceProgram[];
  onClickPromo: () => void;
  onClickWatchReplay: () => void;
  hasReplay?: boolean;
  hasPromo?: boolean;
  hasWagers?: boolean;
  isOptedIn?: boolean;
  isMultileg?: boolean;
  withVideo?: boolean;
}

const ActionButtons = ({
  race,
  races,
  onClickPromo,
  onClickWatchReplay,
  hasReplay,
  hasPromo,
  hasWagers,
  isOptedIn,
  isMultileg,
  withVideo
}: ActionButtonsProps) => {
  const colorTokens = useColorTokens() as ColorTokens;
  const isMobile = useMediaQuery(breakpoints.tablet.max.sm);

  const firstRace = get(races, "[0]");
  const lastRace = races?.find((_, index, { length }) => index + 1 === length);
  const videoFeedback = RaceHeaderUtils.getVideoFeedback(
    race,
    firstRace,
    lastRace,
    true
  );

  const buttonSize = useMemo(() => {
    if (isMultileg && !isMobile && (hasPromo || hasReplay)) {
      return "s";
    }
    return isMobile || isMultileg ? "m" : "l";
  }, [isMobile, isMultileg, hasPromo, hasReplay]);

  const showReplayButton =
    hasReplay &&
    [
      RaceVideoFeedback.AVAILABLE_REPLAY,
      RaceVideoFeedback.NOT_AVAILABLE_REPLAY,
      RaceVideoFeedback.FINISHED_REPLAY,
      RaceVideoFeedback.STARTING_SOON_REPLAY
    ].includes(videoFeedback);

  return (
    <Container data-qa-label="action-buttons-container">
      {hasPromo && (
        <Button
          qaLabel={isOptedIn ? "promo-button-opt-in" : "promo-button"}
          variant={isOptedIn ? "tertiary" : "marketing"}
          onPress={onClickPromo}
          border={
            isOptedIn
              ? `solid 1px ${colorTokens.component.button.tertiary.border.base}`
              : ""
          }
          isStretched
          borderRadius={4}
          size={buttonSize}
          icon={
            <Icon
              name="promo"
              size="s"
              mr="space-3"
              lineColor={colorTokens.content[isOptedIn ? "positive" : "alert"]}
              backgroundColor={
                colorTokens.background[
                  isOptedIn ? "positiveSubtle" : "alertSubtle"
                ]
              }
            />
          }
          width={1}
        >
          {isOptedIn ? `${hasWagers ? "Opted-In" : "Promo Opted-In"}` : "Promo"}
        </Button>
      )}
      {isMobile && showReplayButton && (
        <VideoButton
          qaLabel="watch-replay-button"
          videoFeedback={videoFeedback}
          onClickWatchVideo={onClickWatchReplay}
          withVideo={withVideo}
        />
      )}
    </Container>
  );
};

export default ActionButtons;
